import React, { useEffect, useState } from 'react'
import BreadCrumb from '../../../Common/BreadCrumb';
import { Card, Col, Container, Row, Spinner, Alert } from 'react-bootstrap';
import {useTranslation} from "react-i18next";
import Chart from "react-apexcharts";
import { dashboardAPI } from '../../../services/api';

const AlertReport = () => {
    const {t} = useTranslation();
    document.title=t('AlertReports.title');

    const [stats, setStats] = useState<any>(null);
    const [stores, setStores] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Check if demo user
    const isDemoUser = () => {
        const user = sessionStorage.getItem("authUser");
        if (!user) return false;
        try {
            const userData = JSON.parse(user);
            const token = userData.token || userData.access_token;
            return token && typeof token === 'string' && token.startsWith('demo-token-');
        } catch {
            return false;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (isDemoUser()) {
                setIsLoading(false);
                return; // Use mock data for demo users
            }
            
            setIsLoading(true);
            setError(null);
            try {
                // Fetch dashboard stats for alert data
                const statsResponse: any = await dashboardAPI.getDashboardStats({});
                const statsData = statsResponse?.data || statsResponse;
                setStats(statsData);

                // Fetch stores for store names
                const storesResponse = await dashboardAPI.getStores();
                const storesData = Array.isArray(storesResponse) ? storesResponse : (storesResponse?.data || []);
                setStores(storesData);
            } catch (err: any) {
                console.error('Error fetching alert report:', err);
                setError(err?.message || t('Common.error_loading_data'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    // Prepare scenario series from backend data
    const scenarioSeries = isDemoUser() 
        ? [
            { name: "İadeler", count: 42 },
            { name: "İptaller", count: 30 },
            { name: "Fiyat Değişiklikleri", count: 25 },
            { name: "Toplu Satış", count: 20 },
            { name: "Kritik Saat Satış", count: 15 },
          ]
        : stats?.alertsByScenario
            ? Object.entries(stats.alertsByScenario)
                .sort(([,a]: any, [,b]: any) => b - a)
                .slice(0, 5)
                .map(([name, count]: any) => ({ name, count: Number(count) }))
            : [];

    // Prepare status series from backend data
    const statusSeries = isDemoUser()
        ? {
            series: [51, 34, 15],
            labels: [t("Dashboard.open"), t("Dashboard.in_review"), t("Dashboard.closed")],
          }
        : {
            series: [
                Number(stats?.openAlerts || 0),
                Number(stats?.inReviewAlerts || 0),
                Number(stats?.closedAlerts || 0)
            ],
            labels: [t("Dashboard.open"), t("Dashboard.in_review"), t("Dashboard.closed")],
          };

    // Prepare alerts over time from backend data
    const alertsOverTime = isDemoUser()
        ? {
            series: [
                {
                    name: t("AlertReports.alerts"),
                    data: [3, 8, 4, 10, 12, 7, 5],
                },
            ],
            categories: ["01 Apr", "02 Apr", "03 Apr", "04 Apr", "05 Apr", "06 Apr", "07 Apr"],
          }
        : stats?.dailyAlertTrend
            ? {
                series: [{
                    name: t("AlertReports.alerts"),
                    data: Object.entries(stats.dailyAlertTrend)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([, value]: any) => Number(value))
                }],
                categories: Object.keys(stats.dailyAlertTrend)
                    .sort()
                    .map(date => new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }))
              }
            : {
                series: [{ name: t("AlertReports.alerts"), data: [] }],
                categories: []
              };

    // Prepare store series from backend data
    const storeSeries = isDemoUser()
        ? {
            names: ["Store 001", "Store 002", "Store 003", "Store 004", "Store 005"],
            values: [12, 10, 9, 8, 7],
          }
        : stats?.alertsByStore && stores.length > 0
            ? (() => {
                const storeEntries = Object.entries(stats.alertsByStore)
                    .sort(([,a]: any, [,b]: any) => b - a)
                    .slice(0, 5);
                
                return {
                    names: storeEntries.map(([storeId]: any) => {
                        const store = stores.find(s => s.id === storeId || s.id?.toString() === storeId);
                        return store?.name || storeId;
                    }),
                    values: storeEntries.map(([, count]: any) => Number(count))
                };
              })()
            : {
                names: [],
                values: []
              };

    if (isLoading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="text-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">{t('Common.loading')}</span>
                        </Spinner>
                    </div>
                </Container>
            </div>
        );
    }

    if (error && !isDemoUser()) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('AlertReports.page_title'), path: "/alert-reports"}
                        ]}
                    />

                    <Row className="mb-4">
                        <Col md={6}>
                            <Card>
                                <Card.Body>
                                    <h5 className="card-title">📊 {t("AlertReports.most_scenarios")}</h5>
                                    {scenarioSeries.length > 0 ? (
                                        <Chart
                                            type="bar"
                                            height={300}
                                            series={[{ name: t("AlertReports.alerts"), data: scenarioSeries.map((s) => s.count) }]}
                                            options={{
                                                chart: { toolbar: { show: false } },
                                                xaxis: { categories: scenarioSeries.map((s) => s.name) },
                                                colors: ["#FF4560"],
                                                plotOptions: { bar: { borderRadius: 4 } },
                                                dataLabels: { enabled: false },
                                            }}
                                        />
                                    ) : (
                                        <p className="text-muted text-center py-5">{t('Common.no_data_available')}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card>
                                <Card.Body>
                                    <h5 className="card-title">🚦 {t("AlertReports.total_alert_status")}</h5>
                                    {statusSeries.series.some((v: number) => v > 0) ? (
                                        <Chart
                                            type="donut"
                                            height={300}
                                            series={statusSeries.series}
                                            options={{
                                                labels: statusSeries.labels,
                                                colors: ["#ff4d4f", "#faad14", "#52c41a"],
                                                legend: { position: "bottom" },
                                            }}
                                        />
                                    ) : (
                                        <p className="text-muted text-center py-5">{t('Common.no_data_available')}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col md={6}>
                            <Card>
                                <Card.Body>
                                    <h5 className="card-title">🕒 {t("AlertReports.alert_time")}</h5>
                                    {alertsOverTime.categories.length > 0 ? (
                                        <Chart
                                            type="line"
                                            height={300}
                                            series={alertsOverTime.series}
                                            options={{
                                                chart: { toolbar: { show: false } },
                                                xaxis: { categories: alertsOverTime.categories },
                                                stroke: { curve: "smooth" },
                                                colors: ["#1890ff"],
                                                dataLabels: { enabled: false },
                                            }}
                                        />
                                    ) : (
                                        <p className="text-muted text-center py-5">{t('Common.no_data_available')}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card>
                                <Card.Body>
                                    <h5 className="card-title">🏬 {t("AlertReports.alert_over_store")}</h5>
                                    {storeSeries.values.length > 0 ? (
                                        <Chart
                                            type="bar"
                                            height={300}
                                            series={[{ name: t("AlertReports.alerts"), data: storeSeries.values }]}
                                            options={{
                                                chart: { toolbar: { show: false } },
                                                xaxis: { categories: storeSeries.names },
                                                plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
                                                colors: ["#722ed1"],
                                                dataLabels: { enabled: false },
                                            }}
                                        />
                                    ) : (
                                        <p className="text-muted text-center py-5">{t('Common.no_data_available')}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

        </React.Fragment>
    )



}

export default AlertReport